<script setup lang="ts">
import { reactive, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CommerceAddress } from '../types/commerce';

const DEFAULT_COUNTRY_CODE = 'DE';

const { t } = useI18n({ useScope: 'global' });

const props = defineProps<{
    addressType: 'shipping' | 'billing';
    countriesList: Record<string, string>;
    initialAddress?: Partial<CommerceAddress>;
    errors?: Record<string, string[]>;
}>();

const id = useId();

const address = reactive<CommerceAddress>({
    organization: props.initialAddress?.organization ?? '',
    fullName: props.initialAddress?.fullName ?? '',
    addressLine1: props.initialAddress?.addressLine1 ?? '',
    addressLine2: props.initialAddress?.addressLine2 ?? '',
    countryCode: props.initialAddress?.countryCode ?? DEFAULT_COUNTRY_CODE,
    postalCode: props.initialAddress?.postalCode ?? '',
    locality: props.initialAddress?.locality ?? '',
});

function ac(...tokens: string[]): string {
    return [props.addressType, ...tokens].join(' ');
}

function fieldName(field: keyof CommerceAddress): string {
    return `${props.addressType}Address[${field}]`;
}

function fieldErrors(field: keyof CommerceAddress): string[] {
    return props.errors?.[field] ?? [];
}
</script>

<template>
    <div class="mb-3">
        <label class="form-label" :for="`${id}-organization`">
            {{ t('checkout.details.fields.organization') }}
        </label>
        <input
            :id="`${id}-organization`"
            v-model="address.organization"
            :name="fieldName('organization')"
            type="text"
            :class="['form-control', { 'is-invalid': fieldErrors('organization').length }]"
            :autocomplete="ac('organization')"
            :aria-invalid="fieldErrors('organization').length ? 'true' : undefined"
            :aria-errormessage="fieldErrors('organization').length ? `${id}-organization-error` : undefined"
        />
        <div
            v-if="fieldErrors('organization').length"
            :id="`${id}-organization-error`"
            class="invalid-feedback"
        >
            {{ fieldErrors('organization').join(' ') }}
        </div>
    </div>

    <div class="mb-3">
        <label class="form-label" :for="`${id}-full-name`">
            {{ t('checkout.details.fields.fullName') }}
            <span aria-hidden="true">*</span>
        </label>
        <input
            :id="`${id}-full-name`"
            v-model="address.fullName"
            :name="fieldName('fullName')"
            type="text"
            :class="['form-control', { 'is-invalid': fieldErrors('fullName').length }]"
            required
            :autocomplete="ac('name')"
            :aria-invalid="fieldErrors('fullName').length ? 'true' : undefined"
            :aria-errormessage="fieldErrors('fullName').length ? `${id}-full-name-error` : undefined"
        />
        <div
            v-if="fieldErrors('fullName').length"
            :id="`${id}-full-name-error`"
            class="invalid-feedback"
        >
            {{ fieldErrors('fullName').join(' ') }}
        </div>
    </div>

    <div class="mb-3">
        <label class="form-label" :for="`${id}-address-line1`">
            {{ t('checkout.details.fields.addressLine1') }}
            <span aria-hidden="true">*</span>
        </label>
        <input
            :id="`${id}-address-line1`"
            v-model="address.addressLine1"
            :name="fieldName('addressLine1')"
            type="text"
            :class="['form-control', { 'is-invalid': fieldErrors('addressLine1').length }]"
            required
            :autocomplete="ac('address-line1')"
            :aria-invalid="fieldErrors('addressLine1').length ? 'true' : undefined"
            :aria-errormessage="fieldErrors('addressLine1').length ? `${id}-address-line1-error` : undefined"
        />
        <div
            v-if="fieldErrors('addressLine1').length"
            :id="`${id}-address-line1-error`"
            class="invalid-feedback"
        >
            {{ fieldErrors('addressLine1').join(' ') }}
        </div>
    </div>

    <div class="mb-3">
        <label class="form-label" :for="`${id}-address-line2`">
            {{ t('checkout.details.fields.addressLine2') }}
        </label>
        <input
            :id="`${id}-address-line2`"
            v-model="address.addressLine2"
            :name="fieldName('addressLine2')"
            type="text"
            :class="['form-control', { 'is-invalid': fieldErrors('addressLine2').length }]"
            :autocomplete="ac('address-line2')"
            :aria-invalid="fieldErrors('addressLine2').length ? 'true' : undefined"
            :aria-errormessage="fieldErrors('addressLine2').length ? `${id}-address-line2-error` : undefined"
        />
        <div
            v-if="fieldErrors('addressLine2').length"
            :id="`${id}-address-line2-error`"
            class="invalid-feedback"
        >
            {{ fieldErrors('addressLine2').join(' ') }}
        </div>
    </div>

    <div class="row mb-3">
        <div class="col-md-4 mb-3 mb-md-0">
            <label class="form-label" :for="`${id}-postal-code`">
                {{ t('checkout.details.fields.postalCode') }}
                <span aria-hidden="true">*</span>
            </label>
            <input
                :id="`${id}-postal-code`"
                v-model="address.postalCode"
                :name="fieldName('postalCode')"
                type="text"
                :class="['form-control', { 'is-invalid': fieldErrors('postalCode').length }]"
                required
                :autocomplete="ac('postal-code')"
                :aria-invalid="fieldErrors('postalCode').length ? 'true' : undefined"
                :aria-errormessage="fieldErrors('postalCode').length ? `${id}-postal-code-error` : undefined"
            />
            <div
                v-if="fieldErrors('postalCode').length"
                :id="`${id}-postal-code-error`"
                class="invalid-feedback"
            >
                {{ fieldErrors('postalCode').join(' ') }}
            </div>
        </div>
        <div class="col-md-8">
            <label class="form-label" :for="`${id}-locality`">
                {{ t('checkout.details.fields.locality') }}
                <span aria-hidden="true">*</span>
            </label>
            <input
                :id="`${id}-locality`"
                v-model="address.locality"
                :name="fieldName('locality')"
                type="text"
                :class="['form-control', { 'is-invalid': fieldErrors('locality').length }]"
                required
                :autocomplete="ac('address-level2')"
                :aria-invalid="fieldErrors('locality').length ? 'true' : undefined"
                :aria-errormessage="fieldErrors('locality').length ? `${id}-locality-error` : undefined"
            />
            <div
                v-if="fieldErrors('locality').length"
                :id="`${id}-locality-error`"
                class="invalid-feedback"
            >
                {{ fieldErrors('locality').join(' ') }}
            </div>
        </div>
    </div>

    <div class="mb-3">
        <label class="form-label" :for="`${id}-country`">
            {{ t('checkout.details.fields.countryCode') }}
            <span aria-hidden="true">*</span>
        </label>
        <select
            :id="`${id}-country`"
            v-model="address.countryCode"
            :name="fieldName('countryCode')"
            :class="['form-select', { 'is-invalid': fieldErrors('countryCode').length }]"
            required
            :autocomplete="ac('country')"
            :aria-invalid="fieldErrors('countryCode').length ? 'true' : undefined"
            :aria-errormessage="fieldErrors('countryCode').length ? `${id}-country-error` : undefined"
        >
            <option v-for="(name, code) in countriesList" :key="code" :value="code">{{ name }}</option>
        </select>
        <div
            v-if="fieldErrors('countryCode').length"
            :id="`${id}-country-error`"
            class="invalid-feedback"
        >
            {{ fieldErrors('countryCode').join(' ') }}
        </div>
    </div>
</template>
